import { __makeTemplateObject, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { themeGet } from '@primer/react';
import styled from 'styled-components';
import XIcon from '../../icons/XIcon.js';
import LTText from '../LTText/index.js';
import LTBox from '../LTBox/index.js';
import LTDivider from '../LTDivider/index.js';
import LTIconButton from '../LTIconButton/index.js';
import { addAriaPropertiesOnBackgroundEls } from '../../utils/functions.js';

var LTDrawerPosition;
(function (LTDrawerPosition) {
  LTDrawerPosition["LEFT"] = "left";
  LTDrawerPosition["RIGHT"] = "right";
})(LTDrawerPosition || (LTDrawerPosition = {}));
var Wrapper = styled(LTBox)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  color: ", ";\n  padding: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  color: ", ";\n  padding: ", ";\n"])), themeGet("colors.text.default"), themeGet("space.3"));
var DrawerHeader = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  width: 100%;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  width: 100%;\n"])));
var Content = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  padding: 16px;\n  height: 100%;\n  @media only screen and (max-width: 1024px) {\n    overflow: auto;\n  }\n"], ["\n  padding: 16px;\n  height: 100%;\n  @media only screen and (max-width: 1024px) {\n    overflow: auto;\n  }\n"])));
var Backdrop = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  background-color: rgba(140, 140, 140, 0.2);\n  z-index: 100;\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100vw;\n  opacity: ", ";\n  transition: opacity 0.3s ease-in-out;\n  pointer-events: ", ";\n"], ["\n  background-color: rgba(140, 140, 140, 0.2);\n  z-index: 100;\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100vw;\n  opacity: ", ";\n  transition: opacity 0.3s ease-in-out;\n  pointer-events: ", ";\n"])), function (props) {
  return props.open ? 1 : 0;
}, function (props) {
  return props.open ? "auto" : "none";
});
var Footer = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  bottom: 0px;\n  position: absolute;\n  width: 100%;\n"], ["\n  bottom: 0px;\n  position: absolute;\n  width: 100%;\n"])));
var Drawer = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  background-color: #fff;\n  z-index: 99999;\n  position: fixed;\n  height: ", ";\n  ", "\n  top: ", ";\n  bottom: 0;\n  width: ", "; // Apply dynamic width\n  transform: ", ";\n  transition: transform 0.3s ease-in-out;\n  display: flex;\n  flex-direction: column;\n  box-shadow: 0px 8px 24px 0px rgba(66, 74, 83, 0.12),\n    0px 1px 3px 0px rgba(31, 35, 40, 0.12);\n  border: ", ";\n  border-radius: ", ";\n  border-color: ", ";\n"], ["\n  background-color: #fff;\n  z-index: 99999;\n  position: fixed;\n  height: ", ";\n  ", "\n  top: ", ";\n  bottom: 0;\n  width: ", "; // Apply dynamic width\n  transform: ", ";\n  transition: transform 0.3s ease-in-out;\n  display: flex;\n  flex-direction: column;\n  box-shadow: 0px 8px 24px 0px rgba(66, 74, 83, 0.12),\n    0px 1px 3px 0px rgba(31, 35, 40, 0.12);\n  border: ", ";\n  border-radius: ", ";\n  border-color: ", ";\n"])), function (props) {
  var _a;
  return "calc(100vh - ".concat((_a = props.top) !== null && _a !== void 0 ? _a : 0, "px)");
}, function (props) {
  return props.position === LTDrawerPosition.LEFT ? "left: 0;" : "right: 0;";
}, function (props) {
  var _a;
  return "".concat((_a = props.top) !== null && _a !== void 0 ? _a : 0, "px");
}, function (props) {
  var _a;
  return (_a = props.width) !== null && _a !== void 0 ? _a : "300px";
}, function (props) {
  if (props.position === LTDrawerPosition.LEFT) {
    return props.open ? "translateX(0)" : "translateX(-100%)";
  } else {
    return props.open ? "translateX(0)" : "translateX(100%)";
  }
}, themeGet("borderWidths.1"), function (props) {
  return props.position === LTDrawerPosition.LEFT ? "0px 12px 12px 0px" : "12px 0px 0px 12px";
}, themeGet("colors.border.default"));
var LTDrawer = /*#__PURE__*/forwardRef(function (_a, ref) {
  var bodyStyle = _a.bodyStyle,
    _b = _a.closable,
    closable = _b === void 0 ? true : _b,
    customHeader = _a.customHeader,
    _c = _a.drawerContainer,
    drawerContainer = _c === void 0 ? null : _c,
    footerContent = _a.footerContent,
    footerStyle = _a.footerStyle,
    headerStyle = _a.headerStyle,
    noPortal = _a.noPortal,
    title = _a.title,
    subTitle = _a.subTitle,
    showFooter = _a.showFooter,
    content = _a.content,
    leadingIcon = _a.leadingIcon,
    onClose = _a.onClose,
    width = _a.width,
    _d = _a.position,
    position = _d === void 0 ? LTDrawerPosition.RIGHT : _d,
    top = _a.top;
  var _e = useState(false),
    open = _e[0],
    setOpen = _e[1];
  useEffect(function () {
    if (noPortal) {
      return;
    }
    if (open) {
      addAriaPropertiesOnBackgroundEls(false, drawerContainer);
    } else {
      addAriaPropertiesOnBackgroundEls(true, drawerContainer);
    }
    return function () {
      if (!noPortal) {
        addAriaPropertiesOnBackgroundEls(true, drawerContainer);
      }
    };
  }, [open, noPortal]);
  useImperativeHandle(ref, function () {
    return {
      openDrawer: function openDrawer() {
        return setOpen(true);
      },
      closeDrawer: function closeDrawer() {
        return setOpen(false);
      }
    };
  });
  var showHeader = leadingIcon || title || customHeader || subTitle;
  var drawerContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Backdrop, {
    open: open,
    onClick: function onClick() {
      setOpen(false);
      onClose && onClose();
    }
  }), /*#__PURE__*/React.createElement(Drawer, {
    top: top,
    open: open,
    width: width,
    position: position
  }, showHeader && /*#__PURE__*/React.createElement(Wrapper, null, !!leadingIcon && leadingIcon, /*#__PURE__*/React.createElement(DrawerHeader, {
    style: __assign({
      justifyContent: title ? "space-between" : "end"
    }, headerStyle)
  }, title && /*#__PURE__*/React.createElement(LTText, {
    variant: LTText.TextVariant.SUBHEADER_BOLD
  }, title), customHeader && customHeader, closable && /*#__PURE__*/React.createElement(LTIconButton, {
    ariaLabel: "Close Drawer",
    icon: XIcon,
    variant: LTIconButton.IconButtonVariants.INVISIBLE,
    onClick: function onClick() {
      setOpen(false);
      onClose && onClose();
      if (noPortal) {
        return;
      }
      addAriaPropertiesOnBackgroundEls(true, drawerContainer);
    }
  })), subTitle && /*#__PURE__*/React.createElement(LTText, {
    variant: LTText.TextVariant.SMALL_REGULAR,
    style: {
      color: "#57606A"
    }
  }, subTitle)), showHeader && /*#__PURE__*/React.createElement(LTDivider, null), /*#__PURE__*/React.createElement(Content, {
    style: bodyStyle
  }, content), showFooter && /*#__PURE__*/React.createElement(Footer, null, /*#__PURE__*/React.createElement(LTDivider, null), /*#__PURE__*/React.createElement(Content, {
    style: footerStyle
  }, footerContent))));
  return /*#__PURE__*/React.createElement(React.Fragment, null, noPortal ? drawerContent : /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", {
    className: "lt-modal-wrapper"
  }, drawerContent), drawerContainer || document.body));
});
LTDrawer.Position = LTDrawerPosition;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;

export { LTDrawer as default };
